import React from 'react';
import i18n from 'i18next';
import withGA from 'next-ga';
import NextApp from 'next/app';
import Router from 'next/router';
import { initReactI18next } from 'react-i18next';
import { Provider } from 'react-redux';
import translations from 'resources/translations';
import CookieService from 'services/cookie';
import store from 'store';
import { setApiToken } from 'store/Auth/slice';
import { theme, ThemeProvider } from 'styles';
import 'styles/core.css';
import * as Sentry from '@sentry/nextjs';

interface Props {
  props: any;
}

/**
 * This is a special "page" / component
 * https://nextjs.org/docs/#custom-app
 */
class App extends NextApp<Props> {
  public static async getInitialProps({ Component, ctx }: any) {
    const { isServer, req, query, pathname, asPath } = ctx;

    Sentry.configureScope(scope => {
      scope.setExtra('ssr', isServer);
      scope.setExtra('query', query);
      scope.setExtra('pathname', pathname);
      scope.setExtra('as-path', asPath);

      if (req) {
        scope.setExtra('url', req.url);
        scope.setExtra('method', req.method);
        scope.setExtra('headers', req.headers);
      }
    });

    let pageProps = {};
    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps({ ctx });
    }

    if (req) {
      CookieService.init(req.headers.cookie);
      store.dispatch(setApiToken(CookieService.get('authApiToken')));
    }

    return { pageProps, isServer };
  }

  constructor(props: any, context: any) {
    super(props, context);

    this.setup();
  }

  public render() {
    const { Component, pageProps } = this.props;

    return (
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <Component {...pageProps} />
        </Provider>
      </ThemeProvider>
    );
  }

  private setup() {
    this.setupTranslations();
  }

  /**
   * Setup translations
   * https://react.i18next.com/guides/quick-start#configure-i-18-next
   */
  private setupTranslations() {
    // todo: load more translations
    // https://www.i18next.com/how-to/add-or-load-translations
    i18n
      .use(initReactI18next)
      .init({
        resources: {
          en: {
            translation: translations,
          },
        },
        lng: 'en',
        fallbackLng: 'dev',
        keySeparator: false,
        interpolation: {
          escapeValue: false,
        },
      })
      .catch(err => Sentry.captureException(err));
  }
}

export default withGA(process.env.NEXT_PUBLIC_GA_TRACKING_ID, Router)(App);
